import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { fetchOwnerDashboardStats, fetchStaffDashboardStats } from '@/services/dashboard';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Link } from 'react-router-dom';
import {
  Laptop,
  Package,
  Receipt,
  Wrench,
  ShoppingBag,
  Users,
  ShieldCheck,
  BarChart3,
  TrendingUp,
  DollarSign,
  ScanLine,
  Loader2,
} from 'lucide-react';

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const DashboardPage: React.FC = () => {
  const { profile, role, hasPermission } = useAuth();
  const canViewCosts = hasPermission('purchases');

  const { data: ownerStats, isLoading: loadingOwner, isError: errOwner, error: ownerError, refetch: refreshOwner, dataUpdatedAt: ownerUpdatedAt } = useQuery({
    queryKey: queryKeys.dashboard.stats('owner'),
    queryFn: fetchOwnerDashboardStats,
    enabled: canViewCosts,
  });

  const { data: staffStats, isLoading: loadingStaff, isError: errStaff, error: staffError, refetch: refreshStaff, dataUpdatedAt: staffUpdatedAt } = useQuery({
    queryKey: queryKeys.dashboard.stats('staff'),
    queryFn: fetchStaffDashboardStats,
    enabled: !canViewCosts,
  });

  const isLoading = canViewCosts ? loadingOwner : loadingStaff;
  const isError = canViewCosts ? errOwner : errStaff;
  const error = canViewCosts ? ownerError : staffError;

  return (
    <div className="space-y-6 text-xs">
      {/* Welcome Banner */}
      <div className="bg-white rounded-xl border border-brand-border p-5 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <h1 className="text-xl font-bold text-brand-black">
              Welcome back, {profile?.full_name || 'Staff Member'}
            </h1>
            <span className="text-xs font-bold uppercase tracking-wide bg-brand-green/10 text-brand-green px-2.5 py-0.5 rounded border border-brand-green/20">
              {role || 'staff'}
            </span>
          </div>
          <p className="text-xs text-brand-muted mt-1">
            Al Kuwait Tech — Retail & Repair Management System (PKR / Rs)
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to="/app/scanner"
            className="inline-flex items-center space-x-2 px-3.5 py-2 bg-brand-green text-white text-xs font-bold rounded-md hover:bg-brand-green-hover transition-colors shadow-xs"
          >
            <ScanLine className="w-4 h-4" />
            <span>Barcode & QR Scanner</span>
          </Link>
        </div>
      </div>

      <div className="flex justify-between items-center text-brand-muted">
        <span>{(canViewCosts ? ownerUpdatedAt : staffUpdatedAt) ? `Last refreshed ${new Date(canViewCosts ? ownerUpdatedAt : staffUpdatedAt).toLocaleTimeString()}` : 'Loading shop overview'}</span>
        <button type="button" onClick={() => { void (canViewCosts ? refreshOwner() : refreshStaff()); }} className="font-semibold text-brand-green">Refresh overview</button>
      </div>
      {isError && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => { void (canViewCosts ? refreshOwner() : refreshStaff()); }} />}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {!isError && (ownerStats || staffStats) && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link to="/app/laptops" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"><p className="text-2xl font-bold text-emerald-900">{(canViewCosts ? ownerStats : staffStats)?.laptopsInStock}</p><p className="font-semibold text-emerald-800">Laptops ready to sell →</p></Link>
        <Link to="/app/accessories" className="rounded-xl border border-amber-200 bg-amber-50 p-4"><p className="text-2xl font-bold text-amber-900">{(canViewCosts ? ownerStats : staffStats)?.lowStockCount}</p><p className="font-semibold text-amber-800">Parts at reorder level →</p></Link>
        <Link to="/app/repairs" className="rounded-xl border border-blue-200 bg-blue-50 p-4"><p className="text-2xl font-bold text-blue-900">{(canViewCosts ? ownerStats : staffStats)?.activeRepairsCount}</p><p className="font-semibold text-blue-800">Active repair jobs →</p></Link>
      </div>}
      {/* Live Financial Metrics (Owner & Manager Only) */}
      {canViewCosts && ownerStats && !isError && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-brand-border p-4 shadow-xs">
            <div className="flex justify-between items-center text-brand-muted font-medium">
              <span>Today's Sales ({ownerStats.todaySalesCount})</span>
              <TrendingUp className="w-4 h-4 text-brand-green" />
            </div>
            <p className="text-xl font-bold text-brand-black mt-2">
              {formatCurrency(ownerStats.todayRevenue)}
            </p>
            <span className="text-[10px] text-emerald-600 font-semibold">Invoice totals • UTC calendar day</span>
          </div>

          <div className="bg-white rounded-xl border border-brand-border p-4 shadow-xs">
            <div className="flex justify-between items-center text-brand-muted font-medium">
              <span>This Month Net Revenue</span>
              <DollarSign className="w-4 h-4 text-brand-green" />
            </div>
            <p className="text-xl font-bold text-brand-black mt-2">
              {formatCurrency(ownerStats.monthRevenue)}
            </p>
            <span className="text-[10px] text-brand-muted">Database month • includes returns</span>
          </div>

          <div className="bg-white rounded-xl border border-brand-border p-4 shadow-xs">
            <div className="flex justify-between items-center text-brand-muted font-medium">
              <span>Net Profit (MTD)</span>
              <BarChart3 className="w-4 h-4 text-brand-green" />
            </div>
            <p className={`text-xl font-bold mt-2 ${ownerStats.netProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(ownerStats.netProfit)}
            </p>
            <span className="text-[10px] text-brand-muted">Gross: {formatCurrency(ownerStats.grossProfit)}</span>
          </div>

          <div className="bg-white rounded-xl border border-brand-border p-4 shadow-xs">
            <div className="flex justify-between items-center text-brand-muted font-medium">
              <span>Held Laptop Retail Value</span>
              <Laptop className="w-4 h-4 text-brand-green" />
            </div>
            <p className="text-xl font-bold text-brand-black mt-2">
              {formatCurrency(ownerStats.inventoryRetailValue)}
            </p>
            <span className="text-[10px] text-brand-muted">Cost Value: {formatCurrency(ownerStats.inventoryCostValue)}</span>
          </div>
        </div>
      )}

      {/* Staff Safe Overview Cards (Staff Only) */}
      {!canViewCosts && staffStats && !isError && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-brand-border p-4 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-brand-muted font-medium">Laptops In Stock</p>
              <p className="text-2xl font-bold text-brand-black mt-1">{staffStats.laptopsInStock}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-green-50 text-brand-green flex items-center justify-center">
              <Laptop className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-brand-border p-4 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-brand-muted font-medium">Active Repair Jobs</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">{staffStats.activeRepairsCount}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <Wrench className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-brand-border p-4 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-brand-muted font-medium">Low Stock Accessories</p>
              <p className="text-2xl font-bold text-red-700 mt-1">{staffStats.lowStockCount}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
              <Package className="w-5 h-5" />
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity Lists */}
      {canViewCosts && ownerStats && !isError && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Recent Sales */}
          <div className="bg-white p-4 rounded-xl border border-brand-border space-y-2">
            <h3 className="font-bold text-brand-black uppercase tracking-wider flex items-center justify-between">
              <span>Recent Invoices</span>
              <Link to="/app/sales" className="text-brand-green hover:underline text-[10px]">View All</Link>
            </h3>
            <div className="divide-y divide-brand-border">
              {ownerStats.recentSales.map((s) => (
                <div key={s.id} className="py-2 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-brand-black">#{s.invoice_number}</p>
                    <p className="text-[10px] text-brand-muted">{s.customer?.name ?? 'Walk-in'}</p>
                  </div>
                  <span className="font-bold text-brand-green">{formatCurrency(s.total)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Purchases */}
          <div className="bg-white p-4 rounded-xl border border-brand-border space-y-2">
            <h3 className="font-bold text-brand-black uppercase tracking-wider flex items-center justify-between">
              <span>Recent Purchases</span>
              <Link to="/app/purchases" className="text-brand-green hover:underline text-[10px]">View All</Link>
            </h3>
            <div className="divide-y divide-brand-border">
              {ownerStats.recentPurchases.map((p) => (
                <div key={p.id} className="py-2 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-brand-black">#{p.purchase_number}</p>
                    <p className="text-[10px] text-brand-muted">{p.supplier?.name ?? 'Supplier'}</p>
                  </div>
                  <span className="font-bold text-brand-black">{formatCurrency(p.total)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Repairs */}
          <div className="bg-white p-4 rounded-xl border border-brand-border space-y-2">
            <h3 className="font-bold text-brand-black uppercase tracking-wider flex items-center justify-between">
              <span>Recent Repairs</span>
              <Link to="/app/repairs" className="text-brand-green hover:underline text-[10px]">View All</Link>
            </h3>
            <div className="divide-y divide-brand-border">
              {ownerStats.recentRepairs.map((r) => (
                <div key={r.id} className="py-2 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-brand-black">#{r.job_number}</p>
                    <p className="text-[10px] text-brand-muted">{r.customer?.name ?? 'Customer'}</p>
                  </div>
                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-bold uppercase text-[9px]">
                    {r.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Module Shortcuts Grid */}
      <div>
        <h2 className="text-xs font-bold text-brand-black uppercase tracking-wider mb-3">
          Quick Access Modules
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <Link
            to="/app/inventory/laptops"
            className="bg-white p-4 rounded-xl border border-brand-border hover:border-brand-green transition-all shadow-xs group"
          >
            <div className="w-10 h-10 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-3 group-hover:bg-brand-green group-hover:text-white transition-colors">
              <Laptop className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-bold text-brand-black">Laptop Inventory</h3>
            <p className="text-xs text-brand-muted mt-1">Serialized inventory catalog & QR labels</p>
          </Link>

          <Link
            to="/app/inventory/accessories"
            className="bg-white p-4 rounded-xl border border-brand-border hover:border-brand-green transition-all shadow-xs group"
          >
            <div className="w-10 h-10 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-3 group-hover:bg-brand-green group-hover:text-white transition-colors">
              <Package className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-bold text-brand-black">Accessories</h3>
            <p className="text-xs text-brand-muted mt-1">Stock levels, reorder points & SKUs</p>
          </Link>

          <Link
            to="/app/sales"
            className="bg-white p-4 rounded-xl border border-brand-border hover:border-brand-green transition-all shadow-xs group"
          >
            <div className="w-10 h-10 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-3 group-hover:bg-brand-green group-hover:text-white transition-colors">
              <Receipt className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-bold text-brand-black">POS & Invoices</h3>
            <p className="text-xs text-brand-muted mt-1">Create sales, print receipt & POS counter</p>
          </Link>

          <Link
            to="/app/repairs"
            className="bg-white p-4 rounded-xl border border-brand-border hover:border-brand-green transition-all shadow-xs group"
          >
            <div className="w-10 h-10 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-3 group-hover:bg-brand-green group-hover:text-white transition-colors">
              <Wrench className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-bold text-brand-black">Repairs Kanban</h3>
            <p className="text-xs text-brand-muted mt-1">Track 9-stage repair jobs & parts</p>
          </Link>

          {canViewCosts && (
            <Link
              to="/app/purchases"
              className="bg-white p-4 rounded-xl border border-brand-border hover:border-brand-green transition-all shadow-xs group"
            >
              <div className="w-10 h-10 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-3 group-hover:bg-brand-green group-hover:text-white transition-colors">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-brand-black">Purchases</h3>
              <p className="text-xs text-brand-muted mt-1">Receive stock & landed costs</p>
            </Link>
          )}

          <Link
            to="/app/customers"
            className="bg-white p-4 rounded-xl border border-brand-border hover:border-brand-green transition-all shadow-xs group"
          >
            <div className="w-10 h-10 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-3 group-hover:bg-brand-green group-hover:text-white transition-colors">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-bold text-brand-black">Customers</h3>
            <p className="text-xs text-brand-muted mt-1">Directory & customer contacts</p>
          </Link>

          <Link
            to="/app/warranties"
            className="bg-white p-4 rounded-xl border border-brand-border hover:border-brand-green transition-all shadow-xs group"
          >
            <div className="w-10 h-10 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-3 group-hover:bg-brand-green group-hover:text-white transition-colors">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-bold text-brand-black">Warranties</h3>
            <p className="text-xs text-brand-muted mt-1">Claims, validity & replacements</p>
          </Link>

          {canViewCosts && (
            <Link
              to="/app/reports"
              className="bg-white p-4 rounded-xl border border-brand-border hover:border-brand-green transition-all shadow-xs group"
            >
              <div className="w-10 h-10 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-3 group-hover:bg-brand-green group-hover:text-white transition-colors">
                <BarChart3 className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-brand-black">Financial Reports</h3>
              <p className="text-xs text-brand-muted mt-1">P&L, stock ageing & analytics</p>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
